import type {
  GoodsReceipt,
  GoodsReceiptLine,
  GoodsReceiptStatus,
  MarketOrderSuggestionItem,
  PurchaseOrder,
  PurchaseOrderStatus,
  ReceiptCostStatus,
} from "@station/contracts";
import { Decimal } from "@station/core";
import type { Tone } from "@station/ui";
import { trTextToApiDecimal } from "../pos/pos-values.js";
import { apiDecimalToTrText, isPositiveQuantity } from "./market-values.js";

/**
 * Market SATIN ALMA ekranlarının saf mantığı (Prompt 16 — web, aşama B): sipariş önerisi seçimi → sipariş,
 * sipariş taslağı satırları, sipariş / mal kabul eylemleri ve TABLET mal kabul ekranının durum kuralları.
 *
 * `market-values.ts`'in iki kuralı burada da geçerlidir: ondalık TÜRKİYE biçiminde girilir ve API'ye dizge (nokta)
 * olarak gider; ekranda görünmeyen alan gövdeye ÇIKMAZ.
 */

// ── Durum tonları ────────────────────────────────────────────────────────────
export const PURCHASE_ORDER_STATUS_TONE: Record<PurchaseOrderStatus, Tone> = {
  DRAFT: "neutral",
  PENDING_APPROVAL: "warning",
  APPROVED: "info",
  SENT: "info",
  PARTIALLY_RECEIVED: "warning",
  RECEIVED: "success",
  CLOSED: "neutral",
  REJECTED: "danger",
  CANCELLED: "neutral",
};

export const GOODS_RECEIPT_STATUS_TONE: Record<GoodsReceiptStatus, Tone> = {
  DRAFT: "neutral",
  POSTED: "success",
  CANCELLED: "neutral",
};

export const RECEIPT_COST_STATUS_TONE: Record<ReceiptCostStatus, Tone> = {
  PROVISIONAL: "warning",
  FINAL: "success",
};

/** Alış birim fiyatı / geçici maliyet: KDV hariç, sıfır ya da pozitif, en çok 4 ondalık (sözleşme). */
export function isPurchasePrice(apiDecimal: string): boolean {
  return /^\d{1,12}(\.\d{1,4})?$/.test(apiDecimal);
}

/** Sıfır ya da pozitif miktar (≤ 3 ondalık) — mal kabulde kabul / hasarlı alanı sıfır olabilir. */
export function isNonNegativeQuantity(apiDecimal: string): boolean {
  return /^\d{1,15}(\.\d{1,3})?$/.test(apiDecimal);
}

// ── Sipariş önerisi ──────────────────────────────────────────────────────────
export type SuggestionBlocker = "noSupplier" | "noPrice";

/**
 * Öneri satırı siparişe dönüşebilir mi? Tedarikçisi (ürün – tedarikçi bağı) ya da liste fiyatı olmayan satır
 * dönüştürülemez — sunucu da bu satırları `skipped` ile atlar; ekran nedenini satırda METİNLE söyler.
 */
export function suggestionBlocker(
  item: Pick<MarketOrderSuggestionItem, "supplierId" | "unitPrice">,
): SuggestionBlocker | null {
  if (item.supplierId === null) return "noSupplier";
  if (item.unitPrice === null) return "noPrice";
  return null;
}

/** Önerilen miktarın düzenleme metni (TR). Önerisi sıfır olan satır boş başlar (seçilince miktar yazılır). */
export function suggestionQuantityText(item: Pick<MarketOrderSuggestionItem, "suggestedQuantity">): string {
  return Decimal.from(item.suggestedQuantity).isPositive() ? apiDecimalToTrText(item.suggestedQuantity) : "";
}

export interface SuggestionPick {
  productId: string;
  supplierId: string;
  /** API biçiminde (nokta), 3 ondalık. */
  quantity: string;
  /** Kullanıcı önerilen miktarı değiştirdi mi (ya da önerisi olmayan satıra miktar yazdı mı). */
  edited: boolean;
}

export type SuggestionPickProblem = "blocked" | "quantity";

/**
 * Seçilen öneri satırları → siparişe gidecek seçim. Dönüştürülemeyen satır ve geçersiz miktar AYRI sorunlar olarak
 * döner (ekran satırın altında yazar); sorun varsa sipariş OLUŞTURULMAZ.
 */
export function suggestionPicks(
  items: readonly MarketOrderSuggestionItem[],
  selected: ReadonlySet<string>,
  quantities: Readonly<Record<string, string>>,
): { picks: SuggestionPick[]; problems: Map<string, SuggestionPickProblem> } {
  const picks: SuggestionPick[] = [];
  const problems = new Map<string, SuggestionPickProblem>();
  for (const item of items) {
    if (!selected.has(item.productId)) continue;
    if (suggestionBlocker(item) !== null) {
      problems.set(item.productId, "blocked");
      continue;
    }
    const text = quantities[item.productId] ?? suggestionQuantityText(item);
    const quantity = trTextToApiDecimal(text);
    if (!isPositiveQuantity(quantity)) {
      problems.set(item.productId, "quantity");
      continue;
    }
    const normalized = Decimal.from(quantity).toFixed(3);
    picks.push({
      productId: item.productId,
      supplierId: item.supplierId!,
      quantity: normalized,
      edited: !Decimal.from(normalized).eq(item.suggestedQuantity),
    });
  }
  return { picks, problems };
}

export interface OrderLineInput {
  productId: string;
  quantity: string;
  unitPrice: string | null;
}

// ── Sipariş taslağı satırları ────────────────────────────────────────────────
export interface OrderLineDraft {
  key: string;
  productId: string | null;
  productLabel: string;
  /** TR metin. */
  quantity: string;
  /** TR metin; boş = tedarikçinin liste fiyatı. */
  unitPrice: string;
}

let orderLineSequence = 0;
export function emptyOrderLine(): OrderLineDraft {
  orderLineSequence += 1;
  return {
    key: `order-line-${orderLineSequence}`,
    productId: null,
    productLabel: "",
    quantity: "",
    unitPrice: "",
  };
}

export function orderLineFrom(line: {
  productId: string;
  productCode: string;
  productName: string;
  quantity: string;
  unitPrice: string;
}): OrderLineDraft {
  orderLineSequence += 1;
  return {
    key: `order-line-${orderLineSequence}`,
    productId: line.productId,
    productLabel: `${line.productCode} ${line.productName}`,
    quantity: apiDecimalToTrText(line.quantity),
    unitPrice: apiDecimalToTrText(line.unitPrice),
  };
}

export type OrderLineProblem = "product" | "quantity" | "unitPrice" | "duplicate";

function isBlankOrderLine(line: OrderLineDraft): boolean {
  return !line.productId && line.quantity.trim() === "" && line.unitPrice.trim() === "";
}

/** Sipariş satırı denetimi: ürün, pozitif miktar (≤ 3 ondalık), boş ya da ≥ 0 fiyat (≤ 4 ondalık), tekil ürün. */
export function orderLineProblems(lines: readonly OrderLineDraft[]): Map<string, OrderLineProblem> {
  const problems = new Map<string, OrderLineProblem>();
  const seen = new Set<string>();
  for (const line of lines) {
    if (isBlankOrderLine(line)) continue;
    if (!line.productId) {
      problems.set(line.key, "product");
      continue;
    }
    if (!isPositiveQuantity(trTextToApiDecimal(line.quantity))) {
      problems.set(line.key, "quantity");
      continue;
    }
    if (line.unitPrice.trim() !== "" && !isPurchasePrice(trTextToApiDecimal(line.unitPrice))) {
      problems.set(line.key, "unitPrice");
      continue;
    }
    if (seen.has(line.productId)) problems.set(line.key, "duplicate");
    seen.add(line.productId);
  }
  return problems;
}

/** Satırlar → gövde: boş satır atlanır; boş fiyat `null` (sunucu liste fiyatını kullanır). */
export function orderLinesBody(lines: readonly OrderLineDraft[]): OrderLineInput[] {
  return lines
    .filter((line) => !isBlankOrderLine(line))
    .map((line) => ({
      productId: line.productId ?? "",
      quantity: trTextToApiDecimal(line.quantity),
      unitPrice: line.unitPrice.trim() === "" ? null : trTextToApiDecimal(line.unitPrice),
    }));
}

/** Satır tutarı önizlemesi (kuruş; fiyat boşsa ya da geçersizse null — sunucu liste fiyatını uygular). */
export function orderLineAmountPreview(line: OrderLineDraft): string | null {
  const quantity = trTextToApiDecimal(line.quantity);
  const price = trTextToApiDecimal(line.unitPrice);
  if (!isPositiveQuantity(quantity) || line.unitPrice.trim() === "" || !isPurchasePrice(price)) return null;
  return Decimal.from(quantity).mul(price).mul(100).round(0).toFixed(0);
}

// ── Sipariş eylemleri (durum makinesi — sunucu yine de denetler) ─────────────
export function purchaseOrderActions(order: Pick<PurchaseOrder, "status" | "lines">): {
  edit: boolean;
  submit: boolean;
  send: boolean;
  receive: boolean;
  close: boolean;
  cancel: boolean;
  reopen: boolean;
} {
  const { status } = order;
  const nothingReceived = order.lines.every((line) => Decimal.from(line.receivedQuantity).isZero());
  return {
    edit: status === "DRAFT",
    submit: status === "DRAFT",
    send: status === "APPROVED",
    receive: status === "SENT" || status === "PARTIALLY_RECEIVED",
    close: status === "SENT" || status === "PARTIALLY_RECEIVED" || status === "RECEIVED",
    // Mal kabul yapılmış sipariş iptal edilemez (kapatılır).
    cancel:
      status === "DRAFT" ||
      status === "PENDING_APPROVAL" ||
      status === "APPROVED" ||
      (status === "SENT" && nothingReceived),
    reopen: status === "REJECTED",
  };
}

// ── Mal kabul eylemleri ──────────────────────────────────────────────────────
export function goodsReceiptActions(receipt: Pick<GoodsReceipt, "status" | "matchId" | "lines">): {
  edit: boolean;
  post: boolean;
  cancel: boolean;
  priceSuggestion: boolean;
} {
  const draft = receipt.status === "DRAFT";
  return {
    edit: draft,
    post: draft && receipt.lines.length > 0,
    // Faturayla eşleşmiş kabul iptal edilemez (sunucu 409 döner); düğme hiç çıkmaz, neden metinle yazılır.
    cancel: draft || (receipt.status === "POSTED" && receipt.matchId === null),
    priceSuggestion: receipt.status === "POSTED",
  };
}

/** Kabul satırları arasında SKT sorunu olanlar (sunucunun `expiryWarning` alanı; kayıt 422 ile durur). */
export function expiryProblemLines(lines: readonly Pick<GoodsReceiptLine, "id" | "expiryWarning">[]): {
  required: string[];
  past: string[];
} {
  return {
    required: lines.filter((line) => line.expiryWarning === "REQUIRED").map((line) => line.id),
    past: lines.filter((line) => line.expiryWarning === "PAST").map((line) => line.id),
  };
}

// ── Tablet ekranı: okutma ve ± ───────────────────────────────────────────────
export type ReceiptQuantityField = "receivedQuantity" | "damagedQuantity";

/**
 * `+` / `−` düğmesinin yeni miktarı (API biçimi, 3 ondalık). Miktar sıfırın altına İNEMEZ: `−` sıfırda `null`
 * döner (düğme devre dışıdır). Kesirli miktarda `−` sıfıra kırpılır (0,5 − 1 → 0).
 */
export function steppedQuantity(current: string, delta: 1 | -1): string | null {
  const value = Decimal.from(current);
  if (delta < 0 && !value.isPositive()) return null;
  const next = value.add(delta);
  return (next.isNegative() ? Decimal.from(0) : next).toFixed(3);
}

/**
 * "Hasarlıya taşı": sağlam kabul SIFIRLANIR, hasarlı = hasarlı + sağlam. SKT'si geçmiş ürün sağlam kabul edilemez
 * (422 `expired`); hasarlı miktar stoğa girmez ve SKT denetimine takılmaz.
 */
export function moveToDamagedBody(line: Pick<GoodsReceiptLine, "receivedQuantity" | "damagedQuantity">): {
  receivedQuantity: string;
  damagedQuantity: string;
} {
  return {
    receivedQuantity: "0.000",
    damagedQuantity: Decimal.from(line.damagedQuantity).add(line.receivedQuantity).toFixed(3),
  };
}

export interface ReceiptLineFormValues {
  /** TR metin. */
  receivedQuantity: string;
  damagedQuantity: string;
  lotNo: string;
  /** YYYY-AA-GG ya da null. */
  expiryDate: string | null;
  /** TR metin (KDV hariç ₺, ≤ 4 ondalık). */
  unitCost: string;
  note: string;
}

export function receiptLineFormFrom(line: GoodsReceiptLine): ReceiptLineFormValues {
  return {
    receivedQuantity: apiDecimalToTrText(line.receivedQuantity),
    damagedQuantity: apiDecimalToTrText(line.damagedQuantity),
    lotNo: line.lotNo ?? "",
    expiryDate: line.expiryDate,
    unitCost: apiDecimalToTrText(line.unitCost),
    note: line.note ?? "",
  };
}

export type ReceiptLineFieldProblem = "receivedQuantity" | "damagedQuantity" | "unitCost";

/**
 * Satır düzenleme formu → PUT gövdesi. SKT takibi KAPALI üründe parti / SKT alanları ekranda yoktur ve gövdeye
 * ÇIKMAZ (sunucu takipsiz üründe parti / SKT'yi reddeder). Boş kabul / hasarlı 0 sayılır; boş maliyet geçersizdir
 * (satırın mevcut maliyeti formda dolu gelir).
 */
export function receiptLineBody(
  values: ReceiptLineFormValues,
  trackExpiry: boolean,
): { body: Record<string, unknown>; problems: ReceiptLineFieldProblem[] } {
  const quantity = (text: string) => (text.trim() === "" ? "0" : trTextToApiDecimal(text));
  const received = quantity(values.receivedQuantity);
  const damaged = quantity(values.damagedQuantity);
  const unitCost = trTextToApiDecimal(values.unitCost);
  const problems: ReceiptLineFieldProblem[] = [];
  if (!isNonNegativeQuantity(received)) problems.push("receivedQuantity");
  if (!isNonNegativeQuantity(damaged)) problems.push("damagedQuantity");
  if (!isPurchasePrice(unitCost)) problems.push("unitCost");
  const note = values.note.trim();
  const lotNo = values.lotNo.trim();
  return {
    body: {
      receivedQuantity: received,
      damagedQuantity: damaged,
      unitCost,
      note: note === "" ? null : note,
      ...(trackExpiry ? { lotNo: lotNo === "" ? null : lotNo, expiryDate: values.expiryDate } : {}),
    },
    problems,
  };
}

// ── Fark görünümü ────────────────────────────────────────────────────────────
export type ReceiptDifference = "SHORT" | "OVER" | "NONE" | "UNEXPECTED";

/**
 * Satırın farkı: beklenen yoksa (serbest kabul ya da siparişte olmayan ürün) "beklenmeyen"; aksi hâlde kabul −
 * beklenen işaretine göre eksik / fazla. Hasarlı miktar KABUL SAYILMAZ (stoğa girmez) — sunucu da böyle hesaplar.
 */
export function receiptLineDifference(
  line: Pick<GoodsReceiptLine, "expectedQuantity" | "receivedQuantity">,
): ReceiptDifference {
  if (line.expectedQuantity === null) return "UNEXPECTED";
  const difference = Decimal.from(line.receivedQuantity).sub(line.expectedQuantity);
  if (difference.isZero()) return "NONE";
  return difference.isNegative() ? "SHORT" : "OVER";
}

/**
 * Fark özeti satırlardan YENİDEN toplanır ve sunucunun başlıktaki toplamlarıyla karşılaştırılır (tutarsızlık —
 * olmamalı — ekranda uyarı olur). Eksik = Σ max(0, beklenen − kabul); fazla = Σ max(0, kabul − beklenen) + SİPARİŞLİ
 * kabulde beklenmeyen satırların kabulü (siparişte olmayan ürün "fazla gelen" sayılır). SERBEST (siparişsiz) kabulde
 * beklenti yoktur: fazla / eksik sayılmaz — sunucuyla aynı tanım (`apps/api/src/market/goods-receipts.ts`).
 */
export function receiptDifferenceSummary(
  receipt: Pick<GoodsReceipt, "lines" | "totals" | "purchaseOrderId">,
): {
  expected: string;
  received: string;
  damaged: string;
  short: string;
  over: string;
  unexpectedLines: number;
  consistent: boolean;
} {
  let expected = Decimal.from(0);
  let received = Decimal.from(0);
  let damaged = Decimal.from(0);
  let short = Decimal.from(0);
  let over = Decimal.from(0);
  let unexpectedLines = 0;
  for (const line of receipt.lines) {
    received = received.add(line.receivedQuantity);
    damaged = damaged.add(line.damagedQuantity);
    if (line.expectedQuantity === null) {
      unexpectedLines += 1;
      if (receipt.purchaseOrderId !== null) over = over.add(line.receivedQuantity);
      continue;
    }
    expected = expected.add(line.expectedQuantity);
    const difference = Decimal.from(line.receivedQuantity).sub(line.expectedQuantity);
    if (difference.isNegative()) short = short.add(difference.abs());
    else over = over.add(difference);
  }
  const totals = receipt.totals;
  const consistent =
    expected.eq(totals.expectedQuantity) &&
    received.eq(totals.receivedQuantity) &&
    damaged.eq(totals.damagedQuantity) &&
    short.eq(totals.shortQuantity) &&
    over.eq(totals.overQuantity);
  return {
    expected: expected.toFixed(3),
    received: received.toFixed(3),
    damaged: damaged.toFixed(3),
    short: short.toFixed(3),
    over: over.toFixed(3),
    unexpectedLines,
    consistent,
  };
}

// ── Yeni mal kabul ───────────────────────────────────────────────────────────
export interface NewReceiptValues {
  mode: "order" | "free";
  purchaseOrderId: string | null;
  stationId: string | null;
  /** Serbest kabulde hedef depo (Prompt 18: yıkama kimyasal deposu); null → istasyonun varsayılan deposu. */
  warehouseId?: string | null;
  supplierId: string | null;
  despatchNo: string;
  despatchDate: string | null;
  note: string;
  fillReceived: boolean;
}

export type NewReceiptProblem = "order" | "station" | "supplier";

/**
 * Yeni mal kabul gövdesi. Siparişten açılışta istasyon ve tedarikçi SİPARİŞTENDİR (gövdeye çıkmaz); serbest
 * kabulde "beklenenle doldur" anlamsızdır (beklenen yoktur) ve gövdeye çıkmaz.
 */
export function newReceiptBody(values: NewReceiptValues): {
  body: Record<string, unknown>;
  problems: NewReceiptProblem[];
} {
  const despatchNo = values.despatchNo.trim();
  const note = values.note.trim();
  const common = {
    despatchNo: despatchNo === "" ? null : despatchNo,
    despatchDate: values.despatchDate,
    note: note === "" ? null : note,
  };
  if (values.mode === "order") {
    return {
      body: { purchaseOrderId: values.purchaseOrderId ?? "", fillReceived: values.fillReceived, ...common },
      problems: values.purchaseOrderId ? [] : ["order"],
    };
  }
  const problems: NewReceiptProblem[] = [];
  if (!values.stationId) problems.push("station");
  if (!values.supplierId) problems.push("supplier");
  return {
    body: {
      stationId: values.stationId ?? "",
      ...(values.warehouseId ? { warehouseId: values.warehouseId } : {}),
      supplierId: values.supplierId ?? "",
      ...common,
    },
    problems,
  };
}
